import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI } from '../services/api';
import {
  AUTH_IDLE_TIMEOUT_MS,
  AUTH_STORAGE_KEYS,
  clearAuthStorage,
  getIdleExpiresAt,
  getLastActivityAt,
  getStoredRefreshToken,
  isIdleSessionExpired,
  markSessionActivity
} from '../utils/authSession';

const extractError = (error, fallback) => {
  if (typeof error === 'string') return error;
  return error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    error?.error ||
    fallback;
};

const storeAuthSession = (response) => {
  const token = response.token || response.accessToken || response.data?.token || response.data?.accessToken;
  const refreshToken = response.refreshToken || response.data?.refreshToken;

  if (!token) {
    throw new Error('No token received from server');
  }

  localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, token);
  if (refreshToken) localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, refreshToken);

  return {
    token,
    refreshToken,
    user: response.user || response.data?.user || null,
    lastActivityAt: markSessionActivity()
  };
};

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      lastActivityAt: null,
      logoutReason: null,
      loading: false,

      login: async (credentials) => {
        set({ loading: true });
        try {
          const response = await authAPI.login(credentials);

          if (response.requiresLoginEmailVerification || response.requiresAdminEmailVerification) {
            set({ loading: false, logoutReason: null });
            return {
              success: true,
              requiresLoginEmailVerification: true,
              email: response.email,
              challengeId: response.challengeId,
              expiresInMinutes: response.expiresInMinutes,
              maxAttempts: response.maxAttempts,
              message: response.message
            };
          }

          const session = storeAuthSession(response);
          set({
            user: session.user,
            token: session.token,
            refreshToken: session.refreshToken,
            isAuthenticated: true,
            lastActivityAt: session.lastActivityAt,
            logoutReason: null,
            loading: false
          });

          return { success: true, user: session.user };
        } catch (error) {
          set({ loading: false });
          return { success: false, error: extractError(error, 'Admin login failed. Please try again.') };
        }
      },

      verifyLoginEmail: async (payload) => {
        set({ loading: true });
        try {
          const response = await authAPI.verifyLoginEmail(payload);
          const session = storeAuthSession(response);

          set({
            user: session.user,
            token: session.token,
            refreshToken: session.refreshToken,
            isAuthenticated: true,
            lastActivityAt: session.lastActivityAt,
            logoutReason: null,
            loading: false
          });

          return { success: true, user: session.user };
        } catch (error) {
          set({ loading: false });
          return { success: false, error: extractError(error, 'Admin sign-in verification failed.') };
        }
      },

      resendLoginEmailVerification: async (payload) => {
        try {
          const response = await authAPI.resendLoginEmailVerification(payload);
          return {
            success: true,
            email: response.email,
            challengeId: response.challengeId,
            expiresInMinutes: response.expiresInMinutes,
            maxAttempts: response.maxAttempts,
            message: response.message
          };
        } catch (error) {
          return { success: false, error: extractError(error, 'Could not resend admin sign-in code.') };
        }
      },

      hydrateProfile: async () => {
        if (!get().isAuthenticated) return null;
        try {
          const response = await authAPI.getProfile();
          const user = response.user || response.data?.user;
          if (user) set({ user });
          return user;
        } catch (error) {
          if (error?.status === 401 || error?.status === 403) {
            get().logout({ callApi: false, reason: 'auth_failed' });
          }
          return null;
        }
      },

      logout: (options = {}) => {
        const { callApi = true, reason = 'user_logout' } = options || {};
        const refreshTokenToRevoke = get().refreshToken || getStoredRefreshToken();

        if (callApi && refreshTokenToRevoke) {
          authAPI.logout(refreshTokenToRevoke).catch(() => {});
        }

        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          lastActivityAt: null,
          logoutReason: reason,
          loading: false
        });

        clearAuthStorage();
      },

      updateUser: (userData) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : userData
        }));
      },

      setToken: (token, refreshToken) => {
        if (!token) {
          get().logout({ reason: 'token_cleared' });
          return;
        }

        set((state) => ({
          token,
          refreshToken: refreshToken || state.refreshToken,
          isAuthenticated: true
        }));
      },

      touchSession: (timestamp = Date.now()) => {
        if (!get().isAuthenticated) return null;
        const lastActivityAt = markSessionActivity(timestamp);
        set({ lastActivityAt });
        return lastActivityAt;
      },

      syncLastActivity: (timestamp = getLastActivityAt()) => {
        set({ lastActivityAt: timestamp || null });
        return timestamp || null;
      },

      isSessionExpired: () => isIdleSessionExpired(),
      getSessionExpiresAt: () => getIdleExpiresAt(),

      ensureActiveSession: (reason = 'idle_timeout') => {
        if (get().isAuthenticated && get().isSessionExpired()) {
          get().logout({ reason });
          return false;
        }

        if (get().isAuthenticated && !getLastActivityAt()) {
          get().touchSession();
        }

        return true;
      },

      getAuthState: () => {
        const state = get();
        return {
          isAuthenticated: state.isAuthenticated,
          hasToken: Boolean(state.token),
          hasRefreshToken: Boolean(state.refreshToken || getStoredRefreshToken()),
          hasUser: Boolean(state.user),
          userId: state.user?.id,
          role: state.user?.role,
          adminRole: state.user?.adminRole,
          idleTimeoutMs: AUTH_IDLE_TIMEOUT_MS,
          lastActivityAt: state.lastActivityAt || getLastActivityAt(),
          sessionExpiresAt: getIdleExpiresAt(),
          sessionExpired: state.isSessionExpired()
        };
      },

      setLoading: (loading) => set({ loading })
    }),
    {
      name: 'sanspath-admin-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        lastActivityAt: state.lastActivityAt
      })
    }
  )
);

export default useAuthStore;
